/* ==========================================================================
   frank cart drawer — Impulse 8.x
   Self-contained. Uses Impulse's own documented hooks, never its internals.

   Integration points (all verified against theme.js):
     - document.dispatchEvent('cart:build')  -> theme.CartForm.buildCart()
       Impulse's own "dev-friendly way to build the cart". It re-renders
       [data-products], [data-discounts], [data-subtotal], updates the header
       count, AND calls reInit() to rebind the quantity selectors.
       We must use this instead of swapping innerHTML ourselves: Impulse binds
       theme.QtySelector directly to each .js-qty__wrapper, so any markup we
       injected by hand would have dead +/- buttons.
     - theme.cart.changeItem(key, qty)       -> line item quantity / removal
     - theme.Currency.formatMoney(cents)     -> currency formatting
     - MutationObserver on [data-products]   -> we re-render when the theme does
   ========================================================================== */
(function () {
  'use strict';

  /* ----------------------------------------------------------------------
     CONFIG — tweak everything here
     ---------------------------------------------------------------------- */
  var CONFIG = {
    // Must match the automatic discounts you create in Shopify admin.
    tiers: [
      { qty: 2, pct: 20 },
      { qty: 3, pct: 25 },
      { qty: 4, pct: 30 }
    ],
    unitOne: 'cap',
    unitMany: 'caps',
    msgFirst: "Add {n} {unit} and SAVE {pct}% on your order!",
    msgNext: "You're {n} {unit} away from SAVING AN EXTRA {pct}% on your order!",
    msgMax: "Maximum discount unlocked — {pct}% OFF your whole order!",

    // Product handles that should NOT count toward the tiers (accessories).
    excludeFromCount: ['wooden-cap-retainer'],

    savingsLabel: 'You Saved',

    // Impulse's minus button already removes the line at quantity 1, because
    // cart-item.liquid sets min="0" on the input. Only flip this to true if
    // testing shows the minus button stops at 1 on your theme.
    forceMinusRemovesLine: false
  };

  /* ----------------------------------------------------------------------
     Utilities
     ---------------------------------------------------------------------- */
  var drawer = null;
  var fallbackMoneyFormat = '${{amount}}';

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function routes() {
    return (window.theme && theme.routes) || {};
  }

  function formatMoney(cents) {
    // Prefer the theme's formatter so our prices match the rest of the drawer
    // (including the superscript-cents setting).
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      try { return theme.Currency.formatMoney(cents); } catch (err) { /* fall through */ }
    }
    var value = Number(cents) || 0;
    var fmt = (drawer && drawer.getAttribute('data-money-format')) || fallbackMoneyFormat;
    function num(n, precision, thousands, decimal) {
      var fixed = (n / 100).toFixed(precision);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+$)/g, '$1' + thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount_no_decimals': return num(value, 0, ',', '.');
        case 'amount_with_comma_separator': return num(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return num(value, 0, '.', ',');
        case 'amount_with_space_separator': return num(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return num(value, 0, ' ', ',');
        case 'amount_with_apostrophe_separator': return num(value, 2, "'", '.');
        default: return num(value, 2, ',', '.');
      }
    });
  }

  function giftIcon() {
    return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 7h-2.2a3 3 0 0 0-.5-3.6A3 3 0 0 0 12 4a3 3 0 0 0-5.3-.6A3 3 0 0 0 6.2 7H4a1 1 0 0 0-1 1v3h9V8h0v3h9V8a1 1 0 0 0-1-1ZM4 13v7a1 1 0 0 0 1 1h6v-8H4Zm9 8h6a1 1 0 0 0 1-1v-7h-7v8Z"/></svg>';
  }

  // Ask Impulse to rebuild the cart. This is the ONLY way we mutate the drawer:
  // it re-renders every region and rebinds the quantity selectors.
  function rebuildCart() {
    document.dispatchEvent(new CustomEvent('cart:build'));
  }

  /* ----------------------------------------------------------------------
     Cart state — read straight off the markup Impulse just rendered
     ---------------------------------------------------------------------- */
  function cartItemsEl() {
    return $('[data-products] .cart__items', drawer);
  }

  function eligibleCount() {
    var items = $$('[data-products] .cart__item', drawer);
    if (items.length) {
      return items.reduce(function (sum, el) {
        if (CONFIG.excludeFromCount.indexOf(el.getAttribute('data-product-handle')) !== -1) return sum;
        var input = $('.js-qty__num', el);
        return sum + (input ? parseInt(input.value, 10) || 0 : 1);
      }, 0);
    }
    var wrap = cartItemsEl();
    return wrap ? parseInt(wrap.getAttribute('data-count'), 10) || 0 : 0;
  }

  /* ----------------------------------------------------------------------
     Progress bar
     ---------------------------------------------------------------------- */
  function renderProgress(count) {
    var host = $('[data-frank-progress]', drawer);
    if (!host) return;

    var tiers = CONFIG.tiers;
    var maxQty = tiers[tiers.length - 1].qty;
    var pct = Math.min(count / maxQty, 1) * 100;

    var next = null;
    for (var i = 0; i < tiers.length; i++) {
      if (count < tiers[i].qty) { next = tiers[i]; break; }
    }

    var text;
    if (!next) {
      text = CONFIG.msgMax.replace('{pct}', tiers[tiers.length - 1].pct);
    } else {
      var away = next.qty - count;
      var unit = away === 1 ? CONFIG.unitOne : CONFIG.unitMany;
      var tpl = count === 0 ? CONFIG.msgFirst : CONFIG.msgNext;
      text = tpl.replace('{n}', away).replace('{unit}', unit).replace('{pct}', next.pct);
    }
    text = text.replace(/(\d+\s*%)/g, '<span class="frank-pct">$1</span>');

    var badges = tiers.map(function (t) {
      var pos = (t.qty / maxQty) * 100;
      var reached = count >= t.qty;
      return '<div class="frank-progress__badge' + (reached ? ' is-reached' : '') + '" style="left:' + pos + '%">' +
        giftIcon() +
        '<span class="frank-progress__label">' + t.pct + '% OFF</span>' +
        '</div>';
    }).join('');

    host.innerHTML =
      '<p class="frank-progress__text">' + text + '</p>' +
      '<div class="frank-progress__bar">' +
        '<div class="frank-progress__fill" style="width:' + pct + '%"></div>' +
        badges +
      '</div>';
  }

  /* ----------------------------------------------------------------------
     "You Saved" — value computed in Liquid, emitted on .cart__items
     by templates/cart.ajax.liquid as data-cart-savings
     ---------------------------------------------------------------------- */
  function renderSavings() {
    var row = $('[data-frank-savings]', drawer);
    if (!row) return;

    var wrap = cartItemsEl();
    var saved = wrap ? parseInt(wrap.getAttribute('data-cart-savings'), 10) || 0 : 0;

    if (saved <= 0) { row.hidden = true; return; }

    row.hidden = false;
    $('[data-frank-savings-label]', row).textContent = CONFIG.savingsLabel;
    $('[data-frank-savings-amount]', row).innerHTML = '-' + formatMoney(saved);
  }

  /* ----------------------------------------------------------------------
     Optional: minus at qty 1 removes the line
     Off by default — stock Impulse already does this (min="0" on the input).
     ---------------------------------------------------------------------- */
  function bindQuantityRemoval() {
    if (!CONFIG.forceMinusRemovesLine) return;

    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('.js-qty__adjust--minus');
      if (!btn || !drawer.contains(btn)) return;

      var input = $('.js-qty__num', btn.closest('.js-qty__wrapper') || document.createElement('div'));
      if (!input || (parseInt(input.value, 10) || 0) > 1) return;

      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();

      var key = input.getAttribute('data-id');
      if (key && window.theme && theme.cart) {
        theme.cart.changeItem(key, 0).then(rebuildCart);
      }
    }, true); // capture: run before theme.QtySelector's own handler
  }

  /* ----------------------------------------------------------------------
     Upsells
     ---------------------------------------------------------------------- */
  function upsellVariants(el) {
    var node = $('script[type="application/json"]', el);
    if (!node) return [];
    try { return JSON.parse(node.textContent); } catch (err) { return []; }
  }

  function selectedVariant(el) {
    var variants = upsellVariants(el);
    if (!variants.length) return null;
    var selects = $$('select', el);
    if (!selects.length) return variants[0];
    var chosen = selects.map(function (s) { return s.value; });
    return variants.filter(function (v) {
      return chosen.every(function (val, i) { return v.options[i] === val; });
    })[0] || null;
  }

  function paintUpsell(el) {
    var variant = selectedVariant(el);
    var priceHost = $('[data-frank-upsell-price]', el);
    if (variant && priceHost) {
      var html = '<span class="frank-upsell__now">' + formatMoney(variant.price) + '</span>';
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        html += '<s class="frank-upsell__compare">' + formatMoney(variant.compare_at_price) + '</s>';
      }
      priceHost.innerHTML = html;
    }
    el.setAttribute('data-variant-id', variant ? variant.id : '');
    var add = $('.frank-upsell__add', el);
    if (add) add.disabled = !variant || !variant.available;
  }

  // The upsells are independent quick-add controls and deliberately do NOT
  // mirror cart contents: a product added from the PDP shows up as a normal
  // cart line, and toggling/plus-adding always adds one more unit.
  function removeOneUnit(handle) {
    // Decrement the cart line for this handle by 1 (removes the line at qty 1).
    var lines = $$('[data-products] .cart__item', drawer);
    var match = lines.filter(function (line) {
      return line.getAttribute('data-product-handle') === handle;
    })[0];
    if (!match) return Promise.resolve();
    var input = $('.js-qty__num', match);
    var qty = input ? parseInt(input.value, 10) || 1 : 1;
    var key = match.getAttribute('data-key');
    if (window.theme && theme.cart && typeof theme.cart.changeItem === 'function') {
      return theme.cart.changeItem(key, qty - 1);
    }
    return fetch(routes().cartChange || '/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty - 1 })
    });
  }

  function addVariant(id) {
    var url = routes().cartAdd || '/cart/add.js';
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json'
      },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
    }).then(function (r) { return r.json(); });
  }

  function bindUpsells() {
    var host = $('[data-frank-upsells]', drawer);
    if (!host) return;

    host.addEventListener('change', function (e) {
      var el = e.target.closest('.frank-upsell');
      if (el) paintUpsell(el);
    });

    host.addEventListener('click', function (e) {
      var btn = e.target.closest('.frank-upsell__add, .frank-upsell__toggle');
      if (!btn) return;
      e.preventDefault();

      var el = btn.closest('.frank-upsell');
      if (!el || el.classList.contains('is-busy')) return;

      var isToggle = btn.classList.contains('frank-upsell__toggle');
      var variantId = el.getAttribute('data-variant-id');
      var wasOn = isToggle && btn.classList.contains('is-on');

      var action;
      if (isToggle && wasOn) {
        // Toggling off removes one unit of the upsell's variant.
        action = removeOneUnit(el.getAttribute('data-handle'));
      } else if (variantId) {
        // Plus button and toggle-on both add one more unit — always.
        action = addVariant(variantId);
      } else {
        return;
      }

      el.classList.add('is-busy');

      action
        .then(rebuildCart)
        .catch(function (err) { console.error('[frank-cart]', err); })
        .then(function () {
          el.classList.remove('is-busy');
          if (isToggle) {
            btn.classList.toggle('is-on', !wasOn);
            btn.setAttribute('aria-checked', !wasOn ? 'true' : 'false');
            el.classList.toggle('is-selected', !wasOn);
          }
          // Plus buttons keep their "+" so the item can be added again.
        });
    });

    $$('.frank-upsell', host).forEach(paintUpsell);
  }

  /* ----------------------------------------------------------------------
     Render + observe
     ---------------------------------------------------------------------- */
  var pending = null;

  function render() {
    renderProgress(eligibleCount());
    renderSavings();
  }

  function scheduleRender() {
    clearTimeout(pending);
    pending = setTimeout(render, 30);
  }

  var started = false;
  function init() {
    if (started) return;
    drawer = document.getElementById('CartDrawer');
    if (!drawer) return;
    started = true;

    // Impulse empties and re-appends [data-products] on every cart change.
    var products = $('[data-products]', drawer);
    if (products) {
      new MutationObserver(scheduleRender).observe(products, { childList: true, subtree: true });
    }

    // Belt and braces: the theme also fires these.
    ['cart:updated', 'cart:build', 'cart:open'].forEach(function (evt) {
      document.addEventListener(evt, scheduleRender);
    });

    bindQuantityRemoval();
    bindUpsells();
    scheduleRender();
  }

  // 'page:loaded' is Impulse's own "theme assets are ready" hook.
  document.addEventListener('page:loaded', init);
  if (document.readyState === 'complete') { init(); }
})();
